import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

function HeroHowItWorks() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const lineProgress = useTransform(scrollYProgress, [0, 0.6], [0, 1]);

  const stepIcons = ["sensors", "cloud_sync", "psychology", "dashboard", "notification_important"];
  const labels = t("how_it_works.hero.step_labels", { returnObjects: true }) || [];

  return (
    <section ref={ref} className="relative pt-32 pb-xl overflow-hidden" style={{ position: "relative" }}>
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-cream via-background to-surface-container-low" />
      <div className="absolute inset-0 tech-grid opacity-30" />

      <div className="max-w-[1280px] mx-auto px-6 relative z-10 text-center">
        <motion.span
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-label-caps text-on-tertiary-container bg-tertiary-fixed px-3 py-1 rounded-full mb-6 inline-block"
        >
          {t("how_it_works.hero.eyebrow")}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-h1 text-[40px] md:text-h1 text-primary mb-6"
        >
          {t("how_it_works.hero.title_a")}
          <br />
          <span className="text-gradient">{t("how_it_works.hero.title_accent")}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="font-body-lg text-on-surface-variant max-w-2xl mx-auto mb-16"
        >
          {t("how_it_works.hero.intro")}
        </motion.p>

        <div className="relative h-[120px] mx-auto max-w-3xl">
          <svg viewBox="0 0 600 120" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="flow" x1="0" x2="1">
                <stop offset="0%" stopColor="#90B849" />
                <stop offset="100%" stopColor="#2DD4BF" />
              </linearGradient>
            </defs>
            <motion.path
              d="M 30 80 Q 150 10 300 60 T 570 50"
              fill="none"
              stroke="url(#flow)"
              strokeWidth="3"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, ease: "easeInOut" }}
            />
            <motion.path
              d="M 30 80 Q 150 10 300 60 T 570 50"
              fill="none"
              stroke="url(#flow)"
              strokeWidth="3"
              strokeLinecap="round"
              opacity="0.2"
              style={{ pathLength: lineProgress }}
            />
          </svg>
          {stepIcons.map((icon, i) => {
            const positions = [5, 28, 50, 72, 95];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.18 }}
                style={{ left: `${positions[i]}%` }}
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-brand/30 animate-ping-slow" />
                  <div className="relative w-12 h-12 rounded-full bg-white shadow-lg border-2 border-brand flex items-center justify-center">
                    <span className="material-symbols-outlined text-brand">{icon}</span>
                  </div>
                  <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest text-primary whitespace-nowrap">
                    {labels[i]}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const stepMeta = [
  { num: "01", icon: "sensors" },
  { num: "02", icon: "cloud_sync" },
  { num: "03", icon: "psychology", featured: true },
  { num: "04", icon: "dashboard" },
  { num: "05", icon: "notification_important", danger: true },
];

function StepGrid() {
  const { t } = useTranslation();
  const steps = t("how_it_works.steps", { returnObjects: true }) || [];

  return (
    <section className="py-xl px-6 relative overflow-hidden">
      <div className="max-w-[1280px] mx-auto">
        <div className="hidden lg:block relative py-12">
          <div className="absolute top-[138px] left-0 w-full h-[2px] process-line opacity-20" />
          <div className="grid grid-cols-5 gap-gutter relative z-10">
            {stepMeta.map((s, i) => {
              const data = steps[i] || {};
              return (
                <motion.div
                  key={s.num}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex flex-col items-center text-center"
                >
                  <div className={`w-16 h-16 rounded-full ${s.featured ? "bg-primary-container" : "bg-white"} ${s.danger ? "border-error" : "border-brand"} border-2 flex items-center justify-center mb-6 shadow-lg`}>
                    <span className={`material-symbols-outlined text-3xl ${s.danger ? "text-error" : "text-brand"}`}>{s.icon}</span>
                  </div>
                  <div className={`glass-card p-6 rounded-xl w-full h-full ${s.featured ? "border-brand" : ""}`}>
                    <span className={`font-label-caps mb-2 block ${s.danger ? "text-error" : s.featured ? "text-brand font-bold" : "text-on-tertiary-container"}`}>
                      {s.num} / {data.tag}
                    </span>
                    <h3 className="font-h3 text-primary mb-3">{data.title}</h3>
                    <p className="text-sm text-on-surface-variant">{data.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="lg:hidden flex flex-col gap-8 relative">
          <div className="absolute left-8 top-8 bottom-8 w-[2px] process-line opacity-20" />
          {stepMeta.map((s, i) => {
            const data = steps[i] || {};
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="flex gap-6 relative"
              >
                <div className={`flex-shrink-0 w-16 h-16 rounded-full bg-white border-2 ${s.danger ? "border-error" : "border-brand"} flex items-center justify-center z-10`}>
                  <span className={`material-symbols-outlined ${s.danger ? "text-error" : "text-brand"}`}>{s.icon}</span>
                </div>
                <div className="glass-card p-6 rounded-xl flex-grow">
                  <h3 className="font-h3 text-primary mb-2">{data.title}</h3>
                  <p className="text-sm text-on-surface-variant">{data.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RapidPulse() {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
      <div className="absolute inset-0 tech-grid-dark opacity-30" />
      <div className="absolute top-1/2 right-8 -translate-y-1/2">
        {[0, 0.6, 1.2].map((d) => (
          <motion.div
            key={d}
            initial={{ scale: 0, opacity: 0.7 }}
            animate={{ scale: 4, opacity: 0 }}
            transition={{ duration: 2.4, delay: d, repeat: Infinity, ease: "easeOut" }}
            className="absolute w-16 h-16 rounded-full border-2 border-brand"
          />
        ))}
        <div className="relative w-16 h-16 rounded-full bg-brand/20 backdrop-blur flex items-center justify-center">
          <div className="w-3 h-3 rounded-full bg-brand animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function Bento() {
  const { t } = useTranslation();
  return (
    <section className="py-xl px-6 bg-surface-container-low">
      <div className="max-w-[1280px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-gutter">
        <div className="md:col-span-2 relative group overflow-hidden rounded-xl h-[400px]">
          <img
            alt={t("how_it_works.bento.precision_title")}
            className="absolute inset-0 w-full h-full object-cover grayscale brightness-50 group-hover:scale-105 transition-transform duration-700"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuBEMkRKiAnKRPmfvUGqFQOuhQcRbKlLjEG6s_7w3sFUWPuxqFUEymIt0OkEoatUfC0q9maHPErcsNINQclVDGLjxbrsBt3I1gfaSjnXRqveFDSP2ZMmjxmCQ1FKwmenI6sQesYHcVfCnJKmzH52ZzVUg8bT1dD-sHre-lkCq-EHo0B-lszgq9zD-t5j3i4kttpq74oAU9khmW-g5c7kIHJ3Wf2tlz_jmTNuyfak8ywHXj9M3T8j0kArMdyou9_tfdwx_Hv_jtMAHg"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
          <div className="absolute bottom-8 left-8 text-white">
            <h2 className="font-h2 mb-4">{t("how_it_works.bento.precision_title")}</h2>
            <p className="opacity-80 max-w-md">{t("how_it_works.bento.precision_desc")}</p>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
          className="bg-white p-8 rounded-xl shadow-sm border border-outline-variant flex flex-col justify-between gap-6 relative overflow-hidden"
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand/8 blur-3xl pointer-events-none" />
          <div className="relative z-10">
            <h3 className="font-h3 text-primary mb-2">{t("how_it_works.bento.reliability_title")}</h3>
            <p className="text-sm text-on-surface-variant">{t("how_it_works.bento.reliability_desc")}</p>
            <div className="mt-3 flex gap-1.5">
              {[...Array(12)].map((_, i) => (
                <motion.span key={i} initial={{ scaleY: 0.4 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }} className="block w-1 h-3 bg-brand rounded-full origin-bottom" style={{ opacity: 0.3 + (i / 12) * 0.7 }} />
              ))}
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-primary-container p-8 rounded-xl shadow-sm flex flex-col justify-between relative overflow-hidden"
        >
          <RapidPulse />
          <div className="relative z-10 flex items-center gap-2">
            <span className="material-symbols-outlined icon-fill text-brand text-4xl">bolt</span>
            <span className="px-2 py-0.5 rounded-full bg-brand/20 text-brand text-[10px] font-bold uppercase tracking-widest">Sub-second</span>
          </div>
          <div className="text-white relative z-10 mt-4">
            <h3 className="font-h3 mb-2">{t("how_it_works.bento.rapid_title")}</h3>
            <p className="text-sm opacity-70">{t("how_it_works.bento.rapid_desc")}</p>
          </div>
        </motion.div>
        <div className="md:col-span-2 relative group overflow-hidden rounded-xl h-[400px]">
          <img
            alt={t("how_it_works.bento.eco_title")}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuCQMgg6WwXh9Fw3OjMU1JZDCocYCCTc_qEsqDRe9gRoxkZDxYYmFefJ0U4htiWU_VyLlt611KS_KPkwNlj4gdMnKNZR-zl2tdaCn4n0d07S4CdJ9GsLUFYqJKwijnEHBW5_sFdfBahvoZy3LTw7rOE7yFlKFJ_ZwFS4SUGv-PwXAWawOfqcMWGRFgQCDfVTG0bDWygI-fh2j0tugAopaZ1BAsl-60MOkWmIMYRfOe5gZc2RkuD_DLzAN-de9r_-Oz8324aRIPZg0A"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
          <div className="absolute top-6 right-6 flex flex-col gap-2 items-end">
            {[
              { l: t("how_it_works.bento.metrics.aqi"), v: "62.4", u: "" },
              { l: t("how_it_works.bento.metrics.canopy"), v: "84%", u: t("how_it_works.bento.metrics.intact") },
              { l: t("how_it_works.bento.metrics.ph"), v: "7.2", u: "" },
            ].map((d, i) => (
              <motion.div
                key={d.l}
                initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, delay: i * 0.15 }}
                className="glass-dark px-3 py-2 rounded-lg flex items-center gap-2"
              >
                <span className="text-[9px] uppercase tracking-widest text-zinc-400">{d.l}</span>
                <span className="font-mono text-sm text-brand font-bold">{d.v}</span>
                {d.u && <span className="text-[9px] text-zinc-500">{d.u}</span>}
              </motion.div>
            ))}
          </div>
          <div className="absolute bottom-8 left-8 text-white">
            <span className="inline-block px-3 py-1 rounded-full bg-brand/20 backdrop-blur text-brand text-[10px] font-bold uppercase tracking-widest border border-brand/30 mb-3">
              {t("how_it_works.bento.baseline_badge")}
            </span>
            <h2 className="font-h2 mb-4">{t("how_it_works.bento.eco_title")}</h2>
            <p className="opacity-80 max-w-md">{t("how_it_works.bento.eco_desc")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SystemArchitecture() {
  const { t } = useTranslation();
  const flow = t("how_it_works.architecture.flow", { returnObjects: true }) || [];
  const flowIcons = ["factory", "anchor", "cloud_sync", "settings_ethernet", "monitor_heart"];
  const stepLabel = t("how_it_works.architecture.step_label");

  return (
    <section data-testid="how-system-architecture" className="relative py-xl bg-brand-dark text-white overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-brand/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute inset-0 tech-grid-dark opacity-30" />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="max-w-3xl mb-lg">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand text-[10px] font-bold uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
            {t("how_it_works.architecture.eyebrow")}
          </span>
          <h2 className="font-h2 text-h2 mt-4">
            {t("how_it_works.architecture.title_a")}{" "}
            <span className="bg-gradient-to-r from-brand via-emerald-300 to-teal-300 bg-clip-text text-transparent">
              {t("how_it_works.architecture.title_accent")}
            </span>
          </h2>
          <p className="text-zinc-300 mt-3 max-w-2xl">
            {t("how_it_works.architecture.intro")}
          </p>
        </div>

        <motion.figure
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-2xl overflow-hidden border border-brand/20 bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
        >
          <a href="/imagery/aocems-how-it-works.png" target="_blank" rel="noreferrer" data-testid="architecture-diagram-link" className="block group cursor-zoom-in">
            <img src="/imagery/aocems-how-it-works.png" alt={t("how_it_works.architecture.intro")} className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]" loading="lazy" />
            <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-950/80 backdrop-blur border border-white/15 text-[10px] uppercase tracking-widest text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="material-symbols-outlined text-[14px]">open_in_new</span>
              {t("how_it_works.architecture.open_full")}
            </span>
          </a>
          <figcaption className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            {t("how_it_works.architecture.fig_caption")}
          </figcaption>
        </motion.figure>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-md">
          {flow.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45, delay: i * 0.08 }}
              className="relative bg-zinc-900/60 backdrop-blur border border-zinc-800 rounded-xl p-4 hover:border-brand/50 transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg bg-brand/20 border border-brand/40 flex items-center justify-center">
                  <span className="material-symbols-outlined text-brand text-[18px]">{flowIcons[i]}</span>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-brand font-bold">
                  {stepLabel} {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="font-manrope font-bold text-white text-sm leading-tight">{f.label}</div>
              <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{f.sub}</p>
            </motion.div>
          ))}
        </div>

        <p className="text-xs text-zinc-500 mt-md max-w-3xl">{t("how_it_works.architecture.footnote")}</p>
      </div>
    </section>
  );
}

export default function HowItWorks() {
  return (
    <>
      <HeroHowItWorks />
      <StepGrid />
      <SystemArchitecture />
      <Bento />
    </>
  );
}
